/**
 * Phase 3.93 — Live-Preview-Panel für CreateListingPage.
 *
 * Sticky-Panel auf Desktop, zeigt eine vereinfachte Vorschau der ListingCard,
 * die mit jedem Tastendruck im Formular aktualisiert wird. Das gibt Händlern
 * sofortiges visuelles Feedback, wie ihr Inserat später in der Suche aussehen
 * wird.
 *
 * Feb-2026 (User-Wunsch 27.05.2026) — KEINE eigenen Dismiss-Buttons mehr.
 * Sichtbarkeit + Ausblenden-Optionen werden zentral in der
 * `CreateListingToolbar` gesteuert (gemeinsam mit Wizard + Fortschritt).
 */
import { MapPin, Calendar, Gauge, Zap, Heart, Info } from "lucide-react";
import SectionSidebar from "./SectionSidebar";

function formatPrice(p) {
    const n = Number(p);
    if (!n || isNaN(n)) return "—";
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

export default function LiveListingPreview({ form }) {
    const cover = (form.images && form.images[0]) || null;
    const ezYear = form.first_registration_date
        ? new Date(form.first_registration_date).getFullYear()
        : (form.year || "—");
    const title = form.title || [form.brand, form.model].filter(Boolean).join(" ") || "Inserat-Titel";
    const km = Number(form.mileage) || 0;
    const kw = Number(form.power_kw) || 0;
    const isAuction = form.sale_type === "auction";
    const priceLabel = isAuction ? "Startgebot" : "Preis (B2C)";

    return (
        <aside
            className="hidden lg:block sticky top-24 self-start"
            data-testid="live-preview-panel"
            aria-label="Live-Vorschau"
        >
            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Live-Vorschau
            </div>
            <div className="bg-white border border-gray-200 rounded-sm overflow-hidden shadow-sm">
                {/* Image */}
                <div className="relative aspect-[4/3] bg-gray-100 flex items-center justify-center">
                    {cover ? (
                        <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                        <div className="text-[11px] text-gray-400 uppercase tracking-wider">Bild-Vorschau</div>
                    )}
                    {form.is_top && (
                        <div className="absolute top-2 left-2 bg-[#DC2626] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm shadow-md">★ TOP</div>
                    )}
                    {form.is_accident_vehicle && (
                        <div className="absolute top-2 right-2 bg-gray-700/95 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm shadow-md inline-flex items-center gap-1">
                            <Info className="w-3 h-3" /> Unfallfahrzeug
                        </div>
                    )}
                    <button type="button" className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-white/95 border border-gray-200 flex items-center justify-center" aria-hidden>
                        <Heart className="w-4 h-4 text-gray-600" />
                    </button>
                </div>
                <div className="p-3">
                    <div className="text-xs text-gray-500 mb-1">{[form.brand || "Marke", form.model || "Modell"].join(" · ")}</div>
                    <div className="font-bold text-sm leading-snug line-clamp-2 min-h-[36px]">{title}</div>
                    <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-600">
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> EZ {ezYear}</span>
                        <span className="inline-flex items-center gap-1"><Gauge className="w-3 h-3" /> {km.toLocaleString("de-DE")} km</span>
                        {kw > 0 && <span className="inline-flex items-center gap-1"><Zap className="w-3 h-3" /> {kw} kW</span>}
                    </div>
                    {form.location && (
                        <div className="mt-2 text-[11px] text-gray-500 inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {form.location}
                        </div>
                    )}
                    <div className="mt-3 flex items-end justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500">{priceLabel}</div>
                            <div className="font-display font-black text-xl font-mono-num">{formatPrice(form.price)}</div>
                        </div>
                        {form.condition && (
                            <span className="inline-flex items-center bg-gray-100 text-gray-700 text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-0.5">
                                {form.condition === "Neufahrzeug" ? "Neu" : form.condition === "Vorführfahrzeug" ? "Vorführ" : "Gebraucht"}
                            </span>
                        )}
                    </div>
                </div>
            </div>
            <div className="text-[10px] text-gray-400 mt-2 leading-relaxed">
                So sieht dein Inserat in der Suche aus. Aktualisiert sich automatisch bei jeder Änderung.
            </div>
            {/* Mai-2026 — „Noch fehlend für die Such-Card" entfernt (User-Wunsch). Pflichtfeld-Validierung passiert zentral im Popup beim Speichern. */}
            <div className="mt-3">
                <SectionSidebar form={form} compact />
            </div>
        </aside>
    );
}
